import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY_MEMBER = { name: "", title: "", bio: "", linkedin: "", order: 0, image_url: "", experience: [] };

function TeamImageUpload({ currentUrl, onUploaded }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/api/upload/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(`${API}/api/files/${data.path}`);
    } catch (err) { alert("Upload failed: " + (err.response?.data?.detail || err.message)); }
    setUploading(false);
  };
  return (
    <div className="mb-4">
      <Label className="text-sm font-sans font-medium text-gray-700 mb-2 block">Profile Photo</Label>
      <div className="flex items-center gap-4">
        {currentUrl ? (
          <img src={currentUrl} alt="" className="w-20 h-20 object-cover border border-gray-200 rounded-sm" />
        ) : (
          <div className="w-20 h-20 bg-gray-100 border border-dashed border-gray-300 rounded-sm flex items-center justify-center">
            <ImageIcon className="w-6 h-6 text-gray-300" />
          </div>
        )}
        <div className="space-y-1">
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-1.5 text-sm font-sans text-navy-900 hover:text-gold-500 transition-colors disabled:opacity-50 border border-gray-200 px-3 py-1.5 rounded-sm">
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading..." : "Upload Photo"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
          {currentUrl && <button onClick={() => onUploaded("")} className="text-xs font-sans text-red-400 hover:text-red-600">Remove photo</button>}
          <p className="text-xs text-gray-400 font-sans">JPG, PNG or WebP. Max 5MB.</p>
        </div>
      </div>
    </div>
  );
}

export default function AdminTeam() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMember, setEditMember] = useState(null);
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [expInput, setExpInput] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchTeam = async () => {
    try { const { data } = await axios.get(`${API}/api/team`); setTeam(data); } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchTeam(); }, []);

  const openCreate = () => { setForm(EMPTY_MEMBER); setExpInput(""); setEditMember("new"); };
  const openEdit = (m) => { setForm({ ...EMPTY_MEMBER, ...m }); setExpInput(""); setEditMember(m.id); };
  const close = () => { setEditMember(null); };

  const addExperience = () => {
    if (!expInput.trim()) return;
    setForm({ ...form, experience: [...(form.experience || []), expInput.trim()] });
    setExpInput("");
  };
  const removeExp = (i) => setForm({ ...form, experience: form.experience.filter((_, idx) => idx !== i) });

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editMember === "new") {
        await axios.post(`${API}/api/team`, form);
      } else {
        await axios.put(`${API}/api/team/${editMember}`, form);
      }
      close();
      fetchTeam();
    } catch {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this team member?")) return;
    await axios.delete(`${API}/api/team/${id}`);
    fetchTeam();
  };

  return (
    <div data-testid="admin-team-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-2xl text-navy-900">Team Members</h1>
        <button onClick={openCreate} data-testid="create-team-button" className="flex items-center gap-2 bg-navy-900 text-white px-5 py-2.5 rounded-sm text-sm font-sans font-medium hover:bg-navy-800 transition-colors">
          <Plus className="w-4 h-4" /> Add Member
        </button>
      </div>
      {loading ? <p className="text-gray-400 font-sans text-sm">Loading...</p> : team.length === 0 ? (
        <div className="text-center py-20 border border-gray-200 bg-white"><p className="text-gray-400 font-sans">No team members yet.</p></div>
      ) : (
        <div className="space-y-2">
          {team.map((m) => (
            <div key={m.id} data-testid={`team-item-${m.id}`} className="bg-white border border-gray-200 p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {m.image_url ? (
                  <img src={m.image_url} alt={m.name} className="w-10 h-10 object-cover rounded-sm border border-gray-200" />
                ) : (
                  <div className="w-10 h-10 bg-navy-900 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-serif text-sm">{m.name?.split(" ").map(n => n[0]).join("")}</span>
                  </div>
                )}
                <div>
                  <h3 className="font-sans text-sm font-medium text-navy-900">{m.name}</h3>
                  <p className="text-xs text-gray-400 font-sans">{m.title}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(m)} className="p-2 text-gray-400 hover:text-navy-900 transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(m.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editMember !== null} onOpenChange={(open) => { if (!open) close(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-navy-900">{editMember === "new" ? "Add Team Member" : "Edit Team Member"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <TeamImageUpload currentUrl={form.image_url} onUploaded={v => setForm({ ...form, image_url: v })} />
            <div><Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Full Name</Label><Input data-testid="team-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="border-gray-200 rounded-sm h-10 font-sans" /></div>
            <div><Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Title / Position</Label><Input data-testid="team-title-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="border-gray-200 rounded-sm h-10 font-sans" /></div>
            <div><Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Bio</Label><Textarea data-testid="team-bio-input" value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} className="border-gray-200 rounded-sm font-sans min-h-[100px]" /></div>
            <div>
              <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Experience (Ex-Companies)</Label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(form.experience || []).map((exp, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-xs font-sans bg-gray-100 text-gray-600 px-2 py-1 rounded-sm">
                    Ex-{exp} <button onClick={() => removeExp(i)} className="text-gray-400 hover:text-red-500">&times;</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={expInput} onChange={e => setExpInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addExperience())} placeholder="e.g. HSBC" className="border-gray-200 rounded-sm h-9 font-sans text-sm flex-1" />
                <button onClick={addExperience} className="px-3 py-1.5 text-xs font-sans bg-gray-100 hover:bg-gray-200 rounded-sm transition-colors">Add</button>
              </div>
            </div>
            <div><Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">LinkedIn URL</Label><Input value={form.linkedin} onChange={e => setForm({...form, linkedin: e.target.value})} className="border-gray-200 rounded-sm h-10 font-sans" /></div>
            <div><Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Display Order</Label><Input type="number" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} className="border-gray-200 rounded-sm h-10 font-sans w-24" /></div>
            <div className="flex justify-end gap-3 pt-4">
              <button onClick={close} className="px-5 py-2.5 border border-gray-200 text-gray-600 rounded-sm text-sm font-sans hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleSave} data-testid="save-team-button" disabled={saving} className="px-5 py-2.5 bg-navy-900 text-white rounded-sm text-sm font-sans font-medium hover:bg-navy-800 transition-colors disabled:opacity-50">{saving ? "Saving..." : "Save"}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
